import { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '../services/authService';
import { useLanguage } from './LanguageContext'; // Import useLanguage
import { showToast } from '../utils/toastManager';

const AuthContext = createContext();

// Helper to get cached user data from sessionStorage
const getCachedUser = () => {
  try {
    const cached = sessionStorage.getItem('cachedUser');
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

// Helper to cache user data in sessionStorage
const cacheUser = (user) => {
  try {
    if (user) {
      sessionStorage.setItem('cachedUser', JSON.stringify(user));
    } else {
      sessionStorage.removeItem('cachedUser');
    }
  } catch {
    // Ignore sessionStorage errors
  }
};

// Get initial state with cached user data
const getInitialState = () => {
  const cachedUser = getCachedUser();
  const token = localStorage.getItem('token');
  const hasPaidFromStorage = localStorage.getItem('hasPaid') === 'true';

  // Check is_verified from cached user first, then fallback to hasPaid
  const isVerifiedFromCache = cachedUser?.is_verified === true || cachedUser?.is_verified === 1;
  const isVerified = isVerifiedFromCache || hasPaidFromStorage;

  return {
    user: cachedUser, // Try to restore from cache on refresh
    token,
    isAuthenticated: !!token, // Set authenticated if token exists
    isLoading: true,
    error: null,
    hasPaid: hasPaidFromStorage,
    isVerified, // Use cached user's is_verified if available, otherwise use hasPaid
  };
};

const initialState = getInitialState();

const authReducer = (state, action) => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      // Check is_verified from user object - handle both boolean true and number 1
      const userIsVerified = action.payload.user?.is_verified === true || action.payload.user?.is_verified === 1;
      const isVerifiedValue = action.payload.isVerified !== undefined
        ? action.payload.isVerified
        : userIsVerified;

      // Console log the authenticated user
      if (action.payload.user) {
        // console.log('Authenticated user:', action.payload.user);
      }

      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        hasPaid: action.payload.hasPaid !== undefined ? action.payload.hasPaid : state.hasPaid,
        isVerified: isVerifiedValue,
      };
    case 'PAYMENT_COMPLETED':
      return {
        ...state,
        hasPaid: true,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
        hasPaid: false,
        isVerified: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        hasPaid: false,
        isVerified: false,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'SET_GRADE':
      return {
        ...state,
        user: state.user ? { ...state.user, gradeBand: action.payload.gradeBand, grade: action.payload.grade || null } : state.user,
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const { t } = useLanguage(); // Use useLanguage().t

  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await authService.getProfile();
          const user = response.data;

          if (!isMounted) return;

          // Check payment status from user object
          const hasPaid = user?.has_paid || user?.payment_status === 'completed' || user?.payment_status === 'approved' || localStorage.getItem('hasPaid') === 'true' || false;
          if (hasPaid) {
            localStorage.setItem('hasPaid', 'true');
          } else {
            localStorage.removeItem('hasPaid');
          }

          // Check if user is verified - handle both boolean true and number 1
          const isVerified = user?.is_verified === true || user?.is_verified === 1;

          // Check if user status is active
          // if (user?.status !== 'active') {
          //   // User status is not active - clear token and logout
          //   localStorage.removeItem('token');
          //   localStorage.removeItem('hasPaid');
          //   sessionStorage.removeItem('cachedUser');
          //   dispatch({
          //     type: 'AUTH_FAILURE',
          //     payload: 'Your account is not active. Please contact support.',
          //   });
          //   return;
          // }

          // Cache user data in sessionStorage for refresh persistence
          cacheUser(user);

          // Console log the logged in user
          // console.log('Logged in user:', user);

          // Store user data in context only, not in localStorage
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: {
              user,
              token,
              hasPaid,
              isVerified,
            },
          });
        } catch (error) {
          if (!isMounted) return;

          // Only clear token on 401 (unauthorized) errors
          // For other errors (network, timeout, etc.), keep the token and maintain session
          if (error.response?.status === 401 || error.response?.status === 403) {
            // Token is invalid or expired - clear it to prevent repeated auth attempts
            localStorage.removeItem('token');
            localStorage.removeItem('hasPaid');
            sessionStorage.removeItem('cachedUser');

            // Show session expired toast (deduplicated)
            showToast.error('Session expired. Please login again.');
            
            dispatch({
              type: 'AUTH_FAILURE',
              payload: 'Session expired. Please login again.',
            });
          } else {
            // Network error or other issues - keep token and maintain session
            // console.error('Error verifying session:', error);

            // For network errors or other non-auth errors, keep the token
            // and maintain the session (useful for offline mode and refresh)
            const isNetworkError = !error.response || error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED';

            if (isNetworkError || error.response?.status >= 500 || error.response?.status === 404) {
              // Network/server/404 error - keep token and maintain session
              // Use cached user data if available
              const cachedUser = getCachedUser();
              const cachedHasPaid = localStorage.getItem('hasPaid') === 'true';

              // Check is_verified from cached user first, then fallback to hasPaid
              const cachedIsVerified = cachedUser?.is_verified === true || cachedUser?.is_verified === 1;
              const isVerified = cachedIsVerified || cachedHasPaid;

              // Maintain session with token even if API call fails
              // This allows the user to continue using the app
              dispatch({
                type: 'AUTH_SUCCESS',
                payload: {
                  user: cachedUser, // Use cached user data if available
                  token, // Keep the token
                  hasPaid: cachedHasPaid,
                  isVerified, // Use cached user's is_verified if available
                },
              });
            } else {
              // Other errors - still maintain session with cached data
              const cachedUser = getCachedUser();
              const cachedHasPaid = localStorage.getItem('hasPaid') === 'true';

              // Check is_verified from cached user first, then fallback to hasPaid
              const cachedIsVerified = cachedUser?.is_verified === true || cachedUser?.is_verified === 1;
              const isVerified = cachedIsVerified || cachedHasPaid;

              dispatch({
                type: 'AUTH_SUCCESS',
                payload: {
                  user: cachedUser, // Use cached user data if available
                  token,
                  hasPaid: cachedHasPaid,
                  isVerified, // Use cached user's is_verified if available
                },
              });
            }
          }
        }
      } else {
        if (!isMounted) return;
        dispatch({ type: 'AUTH_FAILURE', payload: null });
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = async (credentials) => {
    dispatch({ type: 'AUTH_START' });
    try {
      const response = await authService.login(credentials);
      const { user, token } = response.data;

      // Check if user is verified - handle both boolean true and number 1
      const isVerified = user?.is_verified === true || user?.is_verified === 1;

      // Check if user status is active
      if (user?.is_active === false || user?.is_active === 0) {
        const errorMessage = t('auth.accountInactive') || 'Your account is not active. Please contact support.';
        dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
        return { success: false, error: errorMessage };
      }

      // Don't allow login if user hasn't paid (is_verified is false)
      if (!isVerified) {
        const errorMessage = 'Please complete your payment before logging in.';
        dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
        return { success: false, error: errorMessage };
      }

      // Only store token in localStorage, not user data
      if (token) {
        localStorage.setItem('token', token);
      }

      // Check payment status from user object or API response
      const hasPaid = user?.has_paid || user?.payment_status === 'completed' || user?.payment_status === 'approved' || response.data.has_paid || false;
      if (!hasPaid) {
        localStorage.removeItem('hasPaid'); // Ensure hasPaid is false
      } else {
        localStorage.setItem('hasPaid', 'true');
      }

      // Cache user data in sessionStorage for refresh persistence
      cacheUser(user);

      // Console log the logged in user
      // console.log('Logged in user:', user);

      // Store user data in context only, not in localStorage
      // User is verified, so login is allowed
      dispatch({ type: 'AUTH_SUCCESS', payload: { user, token, hasPaid, isVerified } });
      return { success: true, isVerified };
    } catch (error) {
      console.error('Login error in AuthContext:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      let errorMessage = 'Login failed';
      
      if (error.response?.status === 422) {
        // Validation errors
        const validationErrors = error.response?.data?.errors;
        if (validationErrors?.email) {
          errorMessage = validationErrors.email[0];
        } else if (validationErrors?.username) {
          errorMessage = validationErrors.username[0];
        } else if (validationErrors?.password) {
          errorMessage = validationErrors.password[0];
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = 'Invalid credentials. Please check your email and password.';
        }
      } else if (error.response?.status === 401) {
        errorMessage = 'Invalid email or password. Please try again.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    dispatch({ type: 'AUTH_START' });
    try {
      const response = await authService.register(userData);

      // Handle different API response structures
      let user, token;
      if (response.data.user) {
        user = response.data.user;
        token = response.data.token;
      } else if (response.data.data) {
        user = response.data.data.user || response.data.data;
        token = response.data.data.token || response.data.token;
      } else {
        // If API returns user data directly
        user = response.data;
        token = response.data.token || localStorage.getItem('token');
      }

      // Only store token in localStorage, not user data
      if (token) {
        localStorage.setItem('token', token);
      }

      // After registration, user hasn't paid yet
      localStorage.removeItem('hasPaid'); // Ensure hasPaid is false
      const hasPaid = false;

      // Check if user is verified (usually false after registration)
      const isVerified = user?.is_verified || false;

      // Cache user data in sessionStorage for refresh persistence
      if (user) {
        cacheUser(user);

        // Console log the registered user
        // console.log('Registered user:', user);

        dispatch({ type: 'AUTH_SUCCESS', payload: { user, token, hasPaid, isVerified } });
      }
      return { success: true };
    } catch (error) {
      // Handle validation errors
      let errorMessage = 'Registration failed';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.errors) {
        // Handle Laravel validation errors
        const errors = error.response.data.errors;
        errorMessage = Object.values(errors).flat().join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }

      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('hasPaid');
    sessionStorage.removeItem('cachedUser'); // Clear cached user on logout
    dispatch({ type: 'LOGOUT' });
  };

  const setGrade = async ({ gradeBand, grade }) => {
    // persist on user profile via mock
    const current = state.user || {};
    const updated = { ...current, gradeBand, grade: grade || null };
    await authService.updateProfile(updated);
    dispatch({ type: 'SET_GRADE', payload: { gradeBand, grade } });
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const setPaymentCompleted = () => {
    localStorage.setItem('hasPaid', 'true');
    dispatch({ type: 'PAYMENT_COMPLETED' });
  };

  const value = {
    ...state,
    login,
    register,
    logout,
    setGrade,
    clearError,
    setPaymentCompleted,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
